import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, tap } from 'rxjs';
import {
  InboxCounts,
  ReturnListRow,
  ReturnStatus,
  ReturnsListFilters,
  ReturnsService,
} from './returns.service';
import { ReturnDetailComponent } from './return-detail/return-detail.component';

const TAB_INBOX_STATUSES: ReturnStatus[] = ['PENDING_APPROVAL', 'REQUIRES_RECONCILIATION'];
const STATUS_OPTIONS: Array<{ value: ReturnStatus; label: string }> = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'REQUIRES_RECONCILIATION', label: 'Needs reconciliation' },
];

/**
 * Lane D — back-office returns inbox and history list. Two-tab UX:
 *
 *   Inbox    → returns that need a manager (PENDING_APPROVAL, REQUIRES_RECONCILIATION)
 *   History  → everything else, filterable
 *
 * Row click opens the drill-down modal (ReturnDetailComponent). The
 * inbox-counts call refreshes on tab switch and after every reconcile so
 * the badge stays current.
 *
 * Design vocabulary follows §10.1: dense table layout, status indicated
 * by a 3px left-border accent (never a 3-column card grid).
 */
@Component({
  selector: 'app-returns',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, NgSelectModule],
  templateUrl: './returns.component.html',
  styleUrl: './returns.component.scss',
})
export class ReturnsComponent implements OnInit {
  private svc = inject(ReturnsService);
  private modal = inject(NgbModal);
  private toastr = inject(ToastrService);
  private router = inject(Router);

  readonly STATUS_OPTIONS = STATUS_OPTIONS;

  // UI state
  tab = signal<'inbox' | 'history'>('inbox');
  rows = signal<ReturnListRow[]>([]);
  total = signal(0);
  pageCount = signal(1);
  isLoading = signal(false);
  inboxCounts = signal<InboxCounts>({ PENDING_APPROVAL: 0, REQUIRES_RECONCILIATION: 0 });

  // History filters (only used on the History tab)
  searchTerm = signal('');
  selectedStatuses = signal<ReturnStatus[]>([]);
  startDate = signal('');
  endDate = signal('');

  // Pagination
  page = signal(1);
  limit = signal(20);

  readonly inboxBadgeTotal = computed(
    () => this.inboxCounts().PENDING_APPROVAL + this.inboxCounts().REQUIRES_RECONCILIATION,
  );

  private searchSubject = new Subject<string>();

  ngOnInit(): void {
    this.refreshInboxCounts();
    this.load();

    // History tab search debounce, identical pattern to offers list.
    this.searchSubject
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        tap(() => this.page.set(1)),
        switchMap(() => {
          this.isLoading.set(true);
          return this.svc.list(
            { page: this.page(), limit: this.limit() },
            this.currentFilters(),
          );
        }),
        catchError((err) => {
          this.toastr.error(err?.error?.message || 'Failed to load returns.');
          this.isLoading.set(false);
          return of({ items: [], total: 0, page: 1, limit: this.limit(), pageCount: 1 });
        }),
      )
      .subscribe((resp) => {
        this.rows.set(resp.items);
        this.total.set(resp.total);
        this.pageCount.set(resp.pageCount);
        this.isLoading.set(false);
      });
  }

  // ── tabs ─────────────────────────────────────────────────────────

  setTab(tab: 'inbox' | 'history'): void {
    this.tab.set(tab);
    this.page.set(1);
    this.load();
  }

  // ── data ─────────────────────────────────────────────────────────

  load(): void {
    this.isLoading.set(true);
    this.svc.list({ page: this.page(), limit: this.limit() }, this.currentFilters()).subscribe({
      next: (resp) => {
        this.rows.set(resp.items);
        this.total.set(resp.total);
        this.pageCount.set(resp.pageCount);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.toastr.error(err?.error?.message || 'Failed to load returns.');
      },
    });
  }

  refreshInboxCounts(): void {
    this.svc.inboxCounts().subscribe({
      next: (counts) => this.inboxCounts.set(counts),
      // Silent on error — the badge just stays at its prior count.
      error: () => undefined,
    });
  }

  // ── filter handlers ─────────────────────────────────────────────

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this.searchSubject.next(value);
  }

  onStatusChange(statuses: ReturnStatus[] | null): void {
    this.selectedStatuses.set(statuses ?? []);
    this.page.set(1);
    this.load();
  }

  onDateRangeChange(): void {
    this.page.set(1);
    this.load();
  }

  clearFilters(): void {
    this.searchTerm.set('');
    this.selectedStatuses.set([]);
    this.startDate.set('');
    this.endDate.set('');
    this.page.set(1);
    this.load();
  }

  // ── pagination ──────────────────────────────────────────────────

  goToPage(p: number): void {
    if (p < 1 || p > this.pageCount()) return;
    this.page.set(p);
    this.load();
  }

  // ── drill-down ──────────────────────────────────────────────────

  openDetail(row: ReturnListRow): void {
    const ref = this.modal.open(ReturnDetailComponent, {
      size: 'xl',
      backdrop: 'static',
      scrollable: true,
    });
    ref.componentInstance.returnId = row.id;
    ref.result
      .then((didChange: any) => {
        if (didChange) {
          this.refreshInboxCounts();
          this.load();
        }
      })
      .catch(() => undefined); // dismissed
  }

  // ── presentational helpers ──────────────────────────────────────

  statusColor(status: ReturnStatus): string {
    switch (status) {
      case 'COMPLETED':
      case 'APPROVED':
        return 'status-ok';
      case 'PENDING_APPROVAL':
        return 'status-pending';
      case 'REQUIRES_RECONCILIATION':
        return 'status-warn';
      case 'REJECTED':
      case 'CANCELLED':
        return 'status-block';
      default:
        return 'status-muted';
    }
  }

  statusLabel(status: ReturnStatus): string {
    return STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status;
  }

  asNumber(value: string | number): number {
    return typeof value === 'number' ? value : Number(value);
  }

  pageWindow(): number[] {
    const total = this.pageCount();
    const p = this.page();
    const start = Math.max(1, p - 2);
    const end = Math.min(total, p + 2);
    const out: number[] = [];
    for (let i = start; i <= end; i++) out.push(i);
    return out;
  }

  // ── internals ───────────────────────────────────────────────────

  private currentFilters(): ReturnsListFilters {
    if (this.tab() === 'inbox') {
      return { status: TAB_INBOX_STATUSES };
    }
    return {
      status: this.selectedStatuses().length ? this.selectedStatuses() : undefined,
      query: this.searchTerm() || undefined,
      startDate: this.startDate() || undefined,
      endDate: this.endDate() || undefined,
    };
  }
}
